"use client"

import * as React from "react"
import { Clock } from "lucide-react"

import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover"

interface TimePickerProps {
    value?: string | null
    onChange?: (time: string | null) => void
    placeholder?: string
    disabled?: boolean
    className?: string
    /** Intervalle des minutes (défaut: 15) */
    minuteStep?: number
}

function parseTime(input: string): string | null {
    const trimmed = input.trim()
    if (!trimmed) return null
    const match = trimmed.match(/^(\d{1,2})[:\s]?(\d{2})$/)
    if (!match) return null
    const h = Math.min(23, parseInt(match[1], 10))
    const m = Math.min(59, parseInt(match[2], 10))
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
}

/**
 * TimePicker — Deux colonnes (heures + minutes) dans un Popover.
 *
 * Keyboard:
 * - ↑/↓ : naviguer dans la colonne active
 * - Tab : passer des heures aux minutes
 * - Enter : sélectionner
 * - Escape : fermer
 * - Double-clic sur le bouton → saisie texte libre
 */
export function TimePicker({
    value,
    onChange,
    placeholder,
    disabled = false,
    className,
    minuteStep = 15,
}: TimePickerProps) {
    const { t } = useTranslation()
    const [open, setOpen] = React.useState(false)
    const [pendingHour, setPendingHour] = React.useState<number | null>(null)
    const [editMode, setEditMode] = React.useState(false)
    const [inputValue, setInputValue] = React.useState("")
    const inputRef = React.useRef<HTMLInputElement>(null)
    const hourRef = React.useRef<HTMLButtonElement>(null)
    const hourColRef = React.useRef<HTMLDivElement>(null)
    const minuteColRef = React.useRef<HTMLDivElement>(null)

    const displayValue = value?.slice(0, 5) ?? null
    const selectedHour = value ? parseInt(value.split(":")[0], 10) : null
    const selectedMinute = value ? parseInt(value.split(":")[1], 10) : null
    const activeHour = pendingHour ?? selectedHour

    const hours = React.useMemo(() => Array.from({ length: 24 }, (_, i) => i), [])
    const minutes = React.useMemo(
        () => Array.from({ length: Math.floor(60 / minuteStep) }, (_, i) => i * minuteStep),
        [minuteStep],
    )

    // Scroll to selected hour when popover opens + focus hour column
    React.useEffect(() => {
        if (open) {
            setPendingHour(null)
            setTimeout(() => {
                hourRef.current?.scrollIntoView({ block: "center" })
                // Focus the selected hour button (or first)
                const target = hourRef.current ?? hourColRef.current?.querySelector("button")
                ;(target as HTMLElement)?.focus()
            }, 50)
        }
    }, [open])

    // Focus input in edit mode
    React.useEffect(() => {
        if (editMode) {
            setInputValue(displayValue ?? "")
            setTimeout(() => { inputRef.current?.focus(); inputRef.current?.select() }, 10)
        }
    }, [editMode, displayValue])

    const handleHourClick = (h: number) => {
        setPendingHour(h)
        // Move focus to first minute button
        setTimeout(() => {
            const firstMinBtn = minuteColRef.current?.querySelector("button") as HTMLElement | null
            firstMinBtn?.focus()
        }, 10)
    }

    const handleMinuteClick = (m: number) => {
        const hour = pendingHour ?? selectedHour ?? 0
        const time = `${String(hour).padStart(2, "0")}:${String(m).padStart(2, "0")}`
        onChange?.(time)
        setPendingHour(null)
        setOpen(false)
    }

    const handleClear = () => {
        onChange?.(null)
        setPendingHour(null)
        setOpen(false)
    }

    const handleEditCommit = () => {
        const parsed = parseTime(inputValue)
        if (parsed) onChange?.(parsed)
        else if (!inputValue.trim()) onChange?.(null)
        setEditMode(false)
    }

    // Arrow key navigation within a column
    const handleColumnKeyDown = (
        e: React.KeyboardEvent,
        column: "hours" | "minutes",
    ) => {
        const container = column === "hours" ? hourColRef.current : minuteColRef.current
        if (!container) return

        const buttons = Array.from(container.querySelectorAll("button")) as HTMLElement[]
        const current = document.activeElement as HTMLElement
        const idx = buttons.indexOf(current)

        if (e.key === "ArrowDown") {
            e.preventDefault()
            const next = idx < buttons.length - 1 ? idx + 1 : 0
            buttons[next]?.focus()
            buttons[next]?.scrollIntoView({ block: "nearest" })
        } else if (e.key === "ArrowUp") {
            e.preventDefault()
            const prev = idx > 0 ? idx - 1 : buttons.length - 1
            buttons[prev]?.focus()
            buttons[prev]?.scrollIntoView({ block: "nearest" })
        } else if (e.key === "Tab" && column === "hours" && !e.shiftKey) {
            // Tab from hours → focus first minute button
            e.preventDefault()
            const firstMinBtn = minuteColRef.current?.querySelector("button") as HTMLElement | null
            firstMinBtn?.focus()
        } else if (e.key === "Tab" && column === "minutes" && e.shiftKey) {
            // Shift+Tab from minutes → focus active hour button
            e.preventDefault()
            const activeBtn = hourColRef.current?.querySelector("[data-active='true']") as HTMLElement | null
            ;(activeBtn ?? hourColRef.current?.querySelector("button") as HTMLElement | null)?.focus()
        } else if (e.key === "Escape") {
            setOpen(false)
        }
    }

    // Edit mode: inline text input for free-form entry
    if (editMode) {
        return (
            <div className={cn("relative", className)}>
                <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <input
                    ref={inputRef}
                    type="text"
                    inputMode="numeric"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onBlur={handleEditCommit}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") { e.preventDefault(); handleEditCommit() }
                        if (e.key === "Escape") setEditMode(false)
                    }}
                    placeholder="HH:MM"
                    maxLength={5}
                    aria-label={t("common.selectTime")}
                    className={cn(
                        "flex h-10 w-full rounded-md border border-input bg-background pl-9 pr-3 py-2 text-base md:text-sm",
                        "ring-offset-background placeholder:text-muted-foreground",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                    )}
                />
            </div>
        )
    }

    return (
        <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setPendingHour(null) }} modal>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    disabled={disabled}
                    onDoubleClick={(e) => { e.preventDefault(); setOpen(false); setEditMode(true) }}
                    aria-label={displayValue ? `${t("common.selectTime")}: ${displayValue}` : t("common.selectTime")}
                    className={cn(
                        "w-full justify-start text-left font-normal h-10",
                        !displayValue && "text-muted-foreground",
                        className,
                    )}
                >
                    <Clock className="mr-2 h-4 w-4 shrink-0" aria-hidden="true" />
                    {/* ⚠️ **Le libellé était un nœud de texte NU dans un bouton
                        `whitespace-nowrap`** : son `min-width: auto` vaut la
                        chaîne ENTIÈRE, insécable. Il ne pouvait donc pas
                        s'élider — seulement DÉBORDER du bouton, et par-dessus
                        ce qui suit. C'est exactement le défaut corrigé sur le
                        `DatePicker` voisin ; le `TimePicker`, son jumeau, est
                        resté tel quel : un lot borné par une liste de fichiers
                        fabrique un défaut à sa frontière.
                        Constaté le 2026-09-01 dans la modale d'immobilisation,
                        où « Sélectionner une heure » réclame ~180 px dans une
                        boîte de 120 px et recouvrait la colonne d'à côté.
                        Recette normalisée du `SelectTrigger` (`select.tsx`) :
                        `flex-1 min-w-0 truncate text-left`. */}
                    <span className="flex-1 min-w-0 truncate text-left">
                        {displayValue ?? (placeholder || t("common.selectTime"))}
                    </span>
                </Button>
            </PopoverTrigger>
            <PopoverContent
                className="w-auto p-0"
                align="start"
                onWheel={(e) => e.stopPropagation()}
            >
                <div className="flex gap-1 p-1" role="group" aria-label={t("common.selectTime")}>
                    {/* Hours */}
                    <div
                        ref={hourColRef}
                        className="h-[220px] overflow-y-auto flex flex-col"
                        style={{ scrollbarWidth: "none" }}
                        role="listbox"
                        aria-label={t("common.hours")}
                        onKeyDown={(e) => handleColumnKeyDown(e, "hours")}
                        onWheel={(e) => e.stopPropagation()}
                    >
                        <div className="px-3 py-1.5 text-[10px] font-medium text-muted-foreground text-center uppercase tracking-wider sticky top-0 bg-popover z-10" aria-hidden="true">
                            {t("common.hours")}
                        </div>
                        {hours.map((h) => (
                            <button
                                key={h}
                                ref={selectedHour === h ? hourRef : undefined}
                                type="button"
                                role="option"
                                aria-selected={activeHour === h}
                                data-active={activeHour === h}
                                onClick={() => handleHourClick(h)}
                                onKeyDown={(e) => { if (e.key === "Enter") handleHourClick(h) }}
                                className={cn(
                                    "px-4 py-1 text-sm text-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                                    activeHour === h
                                        ? "bg-accent text-accent-foreground font-medium"
                                        : "hover:bg-muted",
                                )}
                            >
                                {String(h).padStart(2, "0")}
                            </button>
                        ))}
                    </div>

                    {/* Minutes */}
                    <div
                        ref={minuteColRef}
                        className="h-[220px] overflow-y-auto flex flex-col"
                        style={{ scrollbarWidth: "none" }}
                        role="listbox"
                        aria-label={t("common.minutes")}
                        onKeyDown={(e) => handleColumnKeyDown(e, "minutes")}
                        onWheel={(e) => e.stopPropagation()}
                    >
                        <div className="px-3 py-1.5 text-[10px] font-medium text-muted-foreground text-center uppercase tracking-wider sticky top-0 bg-popover z-10" aria-hidden="true">
                            {t("common.minutes")}
                        </div>
                        {minutes.map((m) => (
                            <button
                                key={m}
                                type="button"
                                role="option"
                                aria-selected={pendingHour === null && selectedMinute === m}
                                onClick={() => handleMinuteClick(m)}
                                onKeyDown={(e) => { if (e.key === "Enter") handleMinuteClick(m) }}
                                className={cn(
                                    "px-4 py-1 text-sm text-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                                    pendingHour === null && selectedMinute === m
                                        ? "bg-accent text-accent-foreground font-medium"
                                        : "hover:bg-muted",
                                )}
                            >
                                {String(m).padStart(2, "0")}
                            </button>
                        ))}
                    </div>
                </div>

                {value && (
                    <div className="px-1 pb-1">
                        <button
                            type="button"
                            onClick={handleClear}
                            className="w-full text-xs text-muted-foreground hover:text-foreground py-1 transition-colors"
                        >
                            {t("common.clear")}
                        </button>
                    </div>
                )}
            </PopoverContent>
        </Popover>
    )
}
